// src/context/EnhancedAuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '../services/apiServices';
import {
  saveAuthData,
  getUserInfo,
  clearAuthData,
  isAuthenticated as checkIsAuthenticated,
  getAccessToken,
  decodeToken
} from '../utils/tokenStorage';
import { initTokenRefreshMonitor, getTokenExpiry } from '../utils/tokenRefresh';

// Create Authentication Context
const AuthContext = createContext(null);

/**
 * Authentication Provider Component
 * Enhanced with better state management and session handling
 */
export const AuthProvider = ({ children }) => {
  // State for authenticated user
  const [user, setUser] = useState(null);
  // State to track loading status
  const [isLoading, setIsLoading] = useState(true);
  // State for any authentication errors
  const [error, setError] = useState(null);
  // State to track token refresh process
  const [isRefreshing, setIsRefreshing] = useState(false);
  // State to track authentication check completion
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  // State for login attempts remaining (for rate limiting display)
  const [loginAttemptsRemaining, setLoginAttemptsRemaining] = useState(null);
  // State for rate limit reset time
  const [rateLimitResetTime, setRateLimitResetTime] = useState(null);
  // State for MFA pending login
  const [mfaPending, setMfaPending] = useState(null);

  /**
   * Check if token needs refresh - DISABLED
   * @returns {boolean} Always returns false as refresh is disabled
   */
  const shouldRefreshToken = useCallback(() => {
    // Token refresh is disabled, so always return false
    return false;
  }, []);

  /**
   * Refresh the access token - DISABLED
   * @returns {Promise<boolean>} Always returns false as refresh is disabled
   */
  const refreshToken = useCallback(async () => {
    console.log('Token refresh has been disabled. Please log in again.');
    clearAuthData();
    setUser(null);
    return false;
  }, []);

  // Listen for silent refresh events
  useEffect(() => {
    const handleSilentRefresh = (event) => {
      // This event listener handles silent refresh events without triggering UI changes
      // No need to reload the page or redirect
      console.log('Silent token refresh event received:', event.detail.success ? 'success' : 'failure');
    };

    const handleSessionExpired = (event) => {
      console.log('Session expired event received in AuthContext:', event.detail?.message);
      clearAuthData();
      setUser(null);
      setAuthCheckComplete(true);
    };

    const handleForceLogout = (event) => {
      console.log('Force logout event received:', event.detail?.message);
      clearAuthData();
      setUser(null);
      setError(event.detail?.message || 'Votre session a été terminée.');
      setAuthCheckComplete(true);
    };

    window.addEventListener('auth:silentRefresh', handleSilentRefresh);
    window.addEventListener('auth:sessionExpired', handleSessionExpired);
    window.addEventListener('auth:forceLogout', handleForceLogout);

    return () => {
      window.removeEventListener('auth:silentRefresh', handleSilentRefresh);
      window.removeEventListener('auth:sessionExpired', handleSessionExpired);
      window.removeEventListener('auth:forceLogout', handleForceLogout);
    };
  }, []);

  // Check authentication status on initial load
  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      try {
        // Check if user info exists locally
        const storedUser = getUserInfo();

        if (storedUser && checkIsAuthenticated()) {
          // If token needs refresh, refresh it silently before proceeding
          if (shouldRefreshToken()) {
            try {
              // Use a completely silent refresh approach
              const refreshSuccess = await refreshToken();
              if (!refreshSuccess) {
                // Handle silently - just update state without redirects
                setUser(null);
                setAuthCheckComplete(true);
                setIsLoading(false);
                return;
              }
            } catch (refreshError) {
              // Handle refresh error silently without page reload
              console.error('Silent token refresh error during auth check');
              clearAuthData();
              setUser(null);
              setAuthCheckComplete(true);
              setIsLoading(false);
              return;
            }
          }

          try {
            // Always use silentAuth=true to prevent page reloads
            const response = await authService.getCurrentUser(true);

            if (response.success) {
              setUser(response.data.user);
            } else {
              // Invalid or expired token - handle silently
              clearAuthData();
              setUser(null);
            }
          } catch (err) {
            // If MFA setup is required, don't log the user out - they still have valid tokens
            if (err.data?.mfaSetupRequired || err.data?.mfaRequired) {
              setMfaPending({ setupRequired: true, email: storedUser?.email });
              if (window.location.pathname !== '/login') {
                window.location.href = '/login';
              }
            } else {
              clearAuthData();
              setUser(null);
            }
          }
        } else {
          // No stored user - handle silently
          clearAuthData();
          setUser(null);
        }
      } catch (error) {
        // Handle any errors silently without page reloads
        console.error('Auth check error occurred');
        clearAuthData();
        setUser(null);
        setError('Authentication error occurred');
      } finally {
        setIsLoading(false);
        setAuthCheckComplete(true);
      }
    };

    checkAuth();
  }, [shouldRefreshToken, refreshToken]);

  // Set up token refresh monitor using our utility
  const tokenRefreshMonitorRef = useRef(null);

  // Token refresh monitoring has been disabled
  useEffect(() => {
    // No automatic token refresh
    console.log('Automatic token refresh has been disabled');
  }, [user]);

  /**
   * Login function - Enhanced with better error handling and rate limit tracking
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} - Login result
   */
  const login = async (email, password) => {
    setIsLoading(true);
    setError(null);

    try {
      // Removed login attempt logging for security
      const response = await authService.login(email, password);

      // Track rate limit info from response
      if (response.rateLimit) {
        const remaining = parseInt(response.rateLimit.remaining, 10);
        if (!isNaN(remaining)) {
          setLoginAttemptsRemaining(remaining);
        }
        // RateLimit-Reset is seconds until reset, not Unix timestamp
        if (response.rateLimit.reset) {
          const secondsUntilReset = parseInt(response.rateLimit.reset, 10);
          setRateLimitResetTime(new Date(Date.now() + secondsUntilReset * 1000));
        }
      }

      // Check if MFA setup is required (admin forced, user hasn't configured yet)
      if (response.mfaRequired && response.mfaSetupRequired) {
        const authData = {
          tokens: response.data.tokens,
          user: response.data.user
        };
        saveAuthData(authData);
        setUser(response.data.user);
        setLoginAttemptsRemaining(null);
        setRateLimitResetTime(null);

        setMfaPending({
          setupRequired: true,
          email
        });
        setIsLoading(false);
        return { success: true, mfaRequired: true, mfaSetupRequired: true };
      }

      // Check if MFA is required (first step of login completed)
      if (response.mfaRequired) {
        setMfaPending({
          tempToken: response.data.tempToken,
          email
        });
        setIsLoading(false);
        return { success: true, mfaRequired: true };
      }

      if (response.success) {
        // Clear rate limit state on successful login
        setLoginAttemptsRemaining(null);
        setRateLimitResetTime(null);

        // Save authentication data
        const authData = {
          tokens: response.data.tokens,
          user: response.data.user
        };

        saveAuthData(authData);
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      // Log without revealing credentials
      console.error('Login error occurred');

      // Handle rate limit info from error
      if (error.rateLimit) {
        const remaining = parseInt(error.rateLimit.remaining, 10);
        if (!isNaN(remaining)) {
          setLoginAttemptsRemaining(remaining);
        }
        // RateLimit-Reset is seconds until reset, not Unix timestamp
        if (error.rateLimit.reset) {
          const secondsUntilReset = parseInt(error.rateLimit.reset, 10);
          setRateLimitResetTime(new Date(Date.now() + secondsUntilReset * 1000));
        }
      }

      // Special handling for rate limited errors
      if (error.isRateLimited) {
        const secondsUntilReset = error.rateLimit?.reset
          ? parseInt(error.rateLimit.reset, 10)
          : 900; // Default 15 minutes
        const waitMinutes = Math.ceil(secondsUntilReset / 60);
        const resetTime = new Date(Date.now() + secondsUntilReset * 1000);
        setRateLimitResetTime(resetTime);
        const errorMessage = `Trop de tentatives de connexion. Veuillez réessayer dans ${waitMinutes} minute(s).`;
        setError(errorMessage);
        return { success: false, error: errorMessage, isRateLimited: true };
      }

      // Special handling for concurrent login (409 Conflict)
      if (error.status === 409 || (error.data?.status === 409)) {
        const errorMessage = error.data?.message || 'Ce compte est déjà connecté dans une autre session.';
        setError(errorMessage);
        return { success: false, error: errorMessage, isConcurrentLogin: true };
      }

      const errorMessage = error.data?.message || error.message || 'Login error';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Confirm login with MFA TOTP code (second step)
   * @param {string} totpCode - 6-digit TOTP code
   * @param {boolean} trustDevice - Whether to trust this device for 30 days
   * @returns {Promise<Object>} - Login result
   */
  const confirmMfaLogin = async (totpCode, trustDevice = false) => {
    if (!mfaPending || !mfaPending.tempToken) {
      setError('Session expirée. Veuillez vous reconnecter.');
      return { success: false, error: 'No pending MFA login' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.mfaConfirmLogin(mfaPending.tempToken, totpCode, trustDevice);

      if (response.success) {
        setMfaPending(null);
        setLoginAttemptsRemaining(null);
        setRateLimitResetTime(null);

        const authData = {
          tokens: response.data.tokens,
          user: response.data.user
        };

        saveAuthData(authData);
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      } else {
        throw new Error(response.message || 'Code TOTP invalide');
      }
    } catch (error) {
      const errorMessage = error.data?.message || error.message || 'Code TOTP invalide';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Cancel MFA login and reset
   */
  const cancelMfaLogin = () => {
    setMfaPending(null);
    setError(null);
  };

  /**
   * Logout function - Enhanced to handle API errors gracefully
   */
  const logout = async () => {
    setIsLoading(true);

    try {
      // Only call API if authenticated (prevents unnecessary calls)
      if (checkIsAuthenticated()) {
        try {
          await authService.logout();
        } catch (error) {
          // Log without revealing token details
          console.error('Logout API error occurred');
        }
      }
    } finally {
      // Always clear local auth data
      clearAuthData();
      setUser(null);
      setIsLoading(false);
    }
  };

  /**
   * Refresh current user data from the server
   */
  const refreshUser = useCallback(async () => {
    if (!checkIsAuthenticated()) return false;

    try {
      const response = await authService.getCurrentUser(true);

      if (response.success) {
        const updatedUser = response.data.user;
        setUser(updatedUser);

        // Update stored user data
        const storedAuthData = getUserInfo();
        if (storedAuthData) {
          saveAuthData({
            tokens: storedAuthData.tokens,
            user: updatedUser
          });
        }

        return true;
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }

    return false;
  }, []);

  /**
   * Check if user has specific role(s)
   * @param {string|string[]} roles - Role(s) to check
   * @returns {boolean} - True if user has at least one of the specified roles
   */
  const checkRole = useCallback((roles) => {
    if (!user) return false;

    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }

    return user.role === roles;
  }, [user]);

  // Context value - computed properties for common role checks
  const contextValue = {
    user,
    isLoading,
    error, // Kept existing 'error'
    login,
    logout,
    refreshToken, // Kept existing 'refreshToken'
    refreshUser,
    checkPermission: () => false, // Added new 'checkPermission' with a placeholder implementation
    isAuthenticated: !!user,
    authCheckComplete,
    checkRole,
    isSuperAdmin: user?.role === 'superadmin',
    isAdmin: ['superadmin', 'admin'].includes(user?.role),
    isAnalyst: user?.role === 'simpleuser',
    isSimpleUser: user?.role === 'simpleuser',
    isViewer: user?.role === 'viewer',
    companyId: user?.companyId || null,
    companyName: user?.companyName || null,
    companyAiEnabled: user?.role === 'superadmin' ? true : (user?.companyAiEnabled !== false),
    // Rate limiting info for login page
    loginAttemptsRemaining,
    rateLimitResetTime,
    // MFA
    mfaPending,
    confirmMfaLogin,
    cancelMfaLogin
  };

  // Listen for refresh requests
  useEffect(() => {
    const handleRefreshRequest = () => {
      refreshUser();
    };
    window.addEventListener('auth:refreshUser', handleRefreshRequest);
    return () => window.removeEventListener('auth:refreshUser', handleRefreshRequest);
  }, [refreshUser]);

  // Initial data refresh to ensure live fields (like companyName) are current
  useEffect(() => {
    if (user && authCheckComplete) {
      refreshUser();
    }
  }, [authCheckComplete]); // Run after initial check is done

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use the auth context
 * @returns {Object} - Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};